function parseHexColor(value) {
  const hex = String(value || "").trim().replace("#", "");
  if (!/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(hex)) return null;

  const expanded = hex.length === 3
    ? hex.split("").map((character) => character + character).join("")
    : hex;

  return [0, 2, 4].map((offset) => parseInt(expanded.slice(offset, offset + 2), 16));
}

function contrastInk(color) {
  const rgb = parseHexColor(color);
  if (!rgb) return "#1c1c1c";

  const luminance = rgb
    .map((channel) => {
      const normalized = channel / 255;
      return normalized <= 0.03928
        ? normalized / 12.92
        : Math.pow((normalized + 0.055) / 1.055, 2.4);
    })
    .reduce((sum, channel, index) => sum + channel * [0.2126, 0.7152, 0.0722][index], 0);

  const darkContrast = (luminance + 0.05) / 0.05;
  const lightLuminance = 0.91;
  const lightContrast = (lightLuminance + 0.05) / (luminance + 0.05);
  return darkContrast >= lightContrast ? "#1c1c1c" : "#fdf4ee";
}

function findLeadVideo(copy) {
  const directWrapper = copy.querySelector(":scope > .for-full-bleed");
  if (directWrapper?.querySelector(".kg-video-card, .kg-embed-card")) return directWrapper;

  const card = copy.querySelector(":scope > .kg-video-card, :scope > .kg-embed-card");
  return card || null;
}

const CREDIT_MARKER_PATTERN = /^\s*\[cr([12])\][\s\u00a0\u200b]*/i;

function stripCreditMarker(block) {
  const match = (block.textContent || "").match(CREDIT_MARKER_PATTERN);
  if (!match) return null;

  let charactersToRemove = match[0].length;
  const walker = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  let textNode = walker.nextNode();

  while (textNode && charactersToRemove > 0) {
    const nextNode = walker.nextNode();
    const removableLength = Math.min(charactersToRemove, textNode.data.length);
    textNode.deleteData(0, removableLength);
    charactersToRemove -= removableLength;
    textNode = nextNode;
  }

  return match[1];
}

function hasCreditContent(block) {
  return Boolean(
    (block.textContent || "").trim()
    || block.querySelector("img, picture, video, audio, iframe, svg, hr")
  );
}

function moveArticleCredits(page, copy) {
  if (!copy) return;

  const creditBlocks = { 1: [], 2: [] };

  Array.from(copy.children).forEach((block) => {
    const creditSlot = stripCreditMarker(block);
    if (!creditSlot) return;

    if (hasCreditContent(block)) {
      creditBlocks[creditSlot].push(block);
    } else {
      block.remove();
    }
  });

  Object.entries(creditBlocks).forEach(([creditSlot, blocks]) => {
    if (!blocks.length) return;

    const destination = page.querySelector(`[data-article-credit-slot="${creditSlot}"]`);
    if (!destination) return;

    destination.replaceChildren(...blocks);
    destination.closest(".article-sidebar__credits, .article-footer__credits")?.removeAttribute("hidden");
    page.classList.add(`has-article-credit-${creditSlot}`);
  });
}

function revealCreditSlot(page, destination, creditSlot) {
  destination
    .closest(".article-sidebar__credits, .article-footer__credits")
    ?.removeAttribute("hidden");
  page.classList.add(`has-article-credit-${creditSlot}`);
}

function moveLegacyArticleCredits(page) {
  const moveMap = window.moveMap || {};

  Object.entries(moveMap).forEach(([sourceSelector, targetSelector]) => {
    let source;
    let destination;

    try {
      source = page.querySelector(sourceSelector);
      destination = page.querySelector(targetSelector);
    } catch (error) {
      return;
    }

    if (!source || !destination) return;

    const creditSlot = destination.dataset.articleCreditSlot;
    if (!creditSlot) return;

    // The marker format is authoritative when both formats are present.
    if (page.classList.contains(`has-article-credit-${creditSlot}`)) {
      source.remove();
      return;
    }

    destination.appendChild(source);
    revealCreditSlot(page, destination, creditSlot);
  });
}

function initArticlePage(page) {
  if (page.dataset.articleBound === "1") {
    page.classList.remove("article-page--credit-pending");
    return;
  }

  const accent = page.dataset.articleAccent;
  if (accent) page.style.setProperty("--article-ink", contrastInk(accent));

  const copy = page.querySelector("[data-article-copy]");
  const sizeButtons = page.querySelectorAll("[data-article-font-size]");

  moveArticleCredits(page, copy);
  moveLegacyArticleCredits(page);

  sizeButtons.forEach((button) => {
    if (button.dataset.articleFontSize === "20") button.classList.add("is-active");
    button.addEventListener("click", () => {
      page.style.setProperty("--article-copy-size", `${button.dataset.articleFontSize}px`);
      sizeButtons.forEach((item) => item.classList.toggle("is-active", item === button));
    });
  });

  const videoSlot = page.querySelector("[data-article-video-slot]");
  const leadVideo = copy && videoSlot ? findLeadVideo(copy) : null;
  if (leadVideo && videoSlot) {
    videoSlot.appendChild(leadVideo);
    videoSlot.classList.add("has-video");
  }

  page.dataset.articleBound = "1";
  page.classList.remove("article-page--credit-pending");
}

export default function initArticleSystem(root = document) {
  const pages = root.matches?.("[data-article-page]")
    ? [root]
    : root.querySelectorAll?.("[data-article-page]") || [];

  pages.forEach(initArticlePage);
}
